// app/javascript/controllers/sleep_markers_controller.js
import { Controller } from "@hotwired/stimulus";
import { maptilersdk } from './sleep_map_controller';
import { mapIcons } from '../icons/map_icons';

export default class extends Controller {
    static targets = ["map", "infoPanel"];

    initialize() {
        this.markersInitialized = false;
        this.townMarkers = [];
        this.poiMarkers = [];
        this.icons = mapIcons;
        this.currentPage = null;
        this.isLoadingPois = false;
        this.pageCoordinates = {};
        this.isPanelLocked = false;
        this.hoveredMarker = null;
        this.defaultZoom = 13;
        this.hoverZoom = 15;
        this.zoomTimeout = null;
        this.isLoading = false;
        this.hoverTimeout = null;
        this.flyTimeout = null;
    }

    connect() {
        console.log("🗺️ SleepMarkersController connecté");
        const url = new URL(window.location.href);
        this.currentPage = url.searchParams.get('page') || '1';
        this.buildPoiUrl();
        
        // Nettoyage initial des marqueurs
        this.cleanupMarkers();
        
        // Vérifie si c'est un chargement Turbo
        const isTurboLoad = document.documentElement.hasAttribute('data-turbo-loading');
        
        if (isTurboLoad) {
            // Pour les chargements Turbo, on nettoie d'abord les marqueurs existants
            this.cleanupMarkers();
            // On attend que la carte soit prête via l'événement map-initialized
            document.addEventListener('map-initialized', this.handleMapInitialized.bind(this), { once: true });
        } else {
            // Pour le chargement initial, on attend normalement
            document.addEventListener('map-initialized', this.handleMapInitialized.bind(this));
        }
    }

    disconnect() {
        this.cleanupMarkers();
        this.map = null;
        this.poiMarkers = [];
        this.townMarkers = [];
        clearTimeout(this.hoverTimeout);
        clearTimeout(this.flyTimeout);
    }

    createLoadingSpinner() {
        const spinner = document.createElement('div');
        spinner.id = 'map-loading-spinner';
        spinner.className = 'hidden absolute inset-0 flex flex-col items-center justify-center bg-white/30 backdrop-blur-sm z-50 transition-opacity duration-300';
        spinner.innerHTML = `
            <svg class="w-12 h-12 text-blue animate-spin" viewBox="0 0 64 64" fill="none"
                xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                <path d="M32 3C35.8083 3 39.5794 3.75011 43.0978 5.20749C46.6163 6.66488 49.8132 8.80101 52.5061 11.4939C55.199 14.1868 57.3351 17.3837 58.7925 20.9022C60.2499 24.4206 61 28.1917 61 32C61 35.8083 60.2499 39.5794 58.7925 43.0978C57.3351 46.6163 55.199 49.8132 52.5061 52.5061C49.8132 55.199 46.6163 57.3351 43.0978 58.7925C39.5794 60.2499 35.8083 61 32 61C28.1917 61 24.4206 60.2499 20.9022 58.7925C17.3837 57.3351 14.1868 55.199 11.4939 52.5061C8.801 49.8132 6.66487 46.6163 5.20749 43.0978C3.7501 39.5794 3 35.8083 3 32C3 28.1917 3.75011 24.4206 5.2075 20.9022C6.66489 17.3837 8.80101 14.1868 11.4939 11.4939C14.1868 8.80099 17.3838 6.66487 20.9022 5.20749C24.4206 3.7501 28.1917 3 32 3L32 3Z" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M32 3C36.5778 3 41.0906 4.08374 45.1692 6.16256C49.2477 8.24138 52.7762 11.2562 55.466 14.9605C58.1558 18.6647 59.9304 22.9531 60.6448 27.4748C61.3591 31.9965 60.9928 36.6232 59.5759 40.9762" stroke="currentColor" stroke-width="5" stroke-linecap="round" stroke-linejoin="round" class="text-yellow"></path>
            </svg>
            <span class="mt-4 text-blue font-medium">Chargement en cours...</span>
        `;

        // Ajoute le spinner au conteneur de la carte
        const mapContainer = this.map.getContainer();
            if (mapContainer) {
            mapContainer.style.position = 'relative';
                mapContainer.appendChild(spinner);
            }
    }

    showLoadingSpinner() {
        const spinner = document.getElementById('map-loading-spinner');
        if (spinner) {
            spinner.classList.remove('hidden');
            spinner.classList.add('flex');
        }
    }

    hideLoadingSpinner() {
        const spinner = document.getElementById('map-loading-spinner');
        if (spinner) {
            spinner.classList.add('hidden');
            spinner.classList.remove('flex');
        }
    }

    buildPoiUrl() {
        const currentUrl = new URL(window.location.href);
        let path = currentUrl.pathname;
        if (!path.endsWith(".json")) path += ".json";
        this.poiUrl = `${path}${currentUrl.search}`.replace(/&/g, "?");
        return this.poiUrl;
    }

    async loadInitialPOIs() {
        if (!this.poiUrl || this.isLoading) {
            console.log("❌ Pas d'URL de POI disponible ou chargement en cours");
            return;
        }

        this.isLoading = true;
        const url = new URL(this.poiUrl, window.location.origin);
        url.searchParams.set("page", this.currentPage);

        console.log("📍 Chargement des POIs pour la page", this.currentPage);
        
        // Affiche le spinner pour tous les chargements
        this.showLoadingSpinner();

        try {
            const response = await fetch(url.toString(), {
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });
            
            if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
            
            const data = await response.json();
            if (!data || data.type !== "FeatureCollection" || !Array.isArray(data.features)) {
                throw new Error("Données invalides");
            }

            this.initialPOIs = data;
            
            if (this.map) {
                // Nettoyage des marqueurs existants avant d'en ajouter de nouveaux
                this.cleanupMarkers();
                
                // Ajoute les marqueurs
                this.addMarkersToMap(data);
                this.markersInitialized = true;
                
                // Calcule les bounds uniquement pour le chargement initial
                if (!document.documentElement.hasAttribute('data-turbo-loading')) {
                this.calculateInitialBounds();
                }
                
                this.checkPoiCards();
            }

        } catch (error) {
            console.error("❌ Erreur lors du chargement des POIs:", error);
        } finally {
            this.isLoading = false;
            // Cache le spinner après un court délai pour éviter le flash
            setTimeout(() => {
            this.hideLoadingSpinner();
            }, 300);
        }
    }

    handleMapInitialized(event) {
        if (!event.detail || !event.detail.map) return;
        
        this.map = event.detail.map;

        // Crée le spinner une fois que la carte est initialisée
        this.createLoadingSpinner();
        
        // Ne fait le resize que si ce n'est pas un chargement Turbo
        if (!document.documentElement.hasAttribute('data-turbo-loading')) {
            this.map.resize();
        }
        
        // Ajoute un écouteur pour le zoom
        this.map.on('zoom', this.handleZoom.bind(this));

        // Charge les POIs une seule fois
        this.loadInitialPOIs();
    }

    handleZoom() {
        const currentZoom = this.map.getZoom();
        // Cache les marqueurs de ville si le zoom est supérieur à 13
        this.townMarkers.forEach(marker => {
            const element = marker.getElement();
            if (element) {
                element.style.display = currentZoom > 13 ? 'none' : 'block';
            }
        });
    }

    addMarkersToMap(geojson) {
        if (!geojson || geojson.type !== "FeatureCollection" || !Array.isArray(geojson.features)) {
            console.error("❌ Données GeoJSON invalides");
            return;
        }
        
        if (!this.map) {
            console.error("❌ La carte n'est pas initialisée");
            return;
        }

            this.cleanupMarkers();

        const townFeatures = geojson.features.filter(f => f.properties?.type === 'town');
        const poiFeatures = geojson.features.filter(f => f.properties?.type !== 'town');

            poiFeatures.forEach(feature => {
            if (!feature.geometry?.coordinates) return;
            // Ajuste la position du marqueur
            feature.geometry.coordinates = this.adjustMarkerPosition(feature);
                    const marker = this.createPOIMarker(feature);
                    if (marker) {
                        marker.addTo(this.map);
                        this.poiMarkers.push(marker);
                }
            });
        
            townFeatures.forEach(feature => {
            if (!feature.geometry?.coordinates) return;
                    const marker = this.createTownMarker(feature);
                    if (marker) {
                        marker.addTo(this.map);
                        this.townMarkers.push(marker);
            }
        });
    }

    adjustMarkerPosition(feature) {
            const coordinates = feature.geometry.coordinates;
        if (!this.areValidCoordinates(coordinates)) return coordinates;

        // Utilise l'ID du feature comme seed pour générer un décalage constant
        const seed = feature.properties.id || Math.random();
        const random = this.seededRandom(seed);

        const [lng, lat] = coordinates;
        const adjustedCoordinates = [
            lng + (random() - 0.5) * 0.0005,
            lat + (random() - 0.5) * 0.0005
        ];

        return adjustedCoordinates;
    }

    // Fonction pour générer des nombres aléatoires constants basés sur un seed
    seededRandom(seed) {
        const x = Math.sin(seed) * 10000;
        return () => {
            const val = Math.abs(x - Math.floor(x));
            return val;
        };
    }

    createPOIMarker(feature) {
        const coordinates = feature.geometry.coordinates;
        if (!this.areValidCoordinates(coordinates)) return;

        const el = document.createElement('div');
        el.className = 'poi-marker';
        el.setAttribute('data-poi-id', feature.properties.id);
        el.innerHTML = `
            <div class="relative group transition-all duration-300 cursor-pointer">
                ${this.icons[feature.properties.type] || '<div class="w-8 h-8 bg-blue rounded-full"></div>'}
                <div class="absolute w-12 text-center -top-5 -right-2 text-sm font-semibold py-0.5 
                    rounded-lg shadow-xl bg-white text-blue border border-blue 
                    transition-all duration-300 ease-in-out">
                    ${feature.properties.price ? `${feature.properties.price}€` : 'NC'}
                </div>
            </div>`;

        const marker = new maptilersdk.Marker({
            element: el,
            anchor: 'center'
        }).setLngLat(coordinates);

        marker._feature = feature;
        
        // Gestion du hover sur le marker avec délai
        el.addEventListener('mouseenter', () => {
            clearTimeout(this.hoverTimeout);
            this.hoverTimeout = setTimeout(() => {
                this.hoverMarker(marker);
                if (!this.isPanelLocked) {
                this.displayInfoPanel(feature, false);
            }
            }, 150); // Délai de 150ms
        });

        el.addEventListener('mouseleave', () => {
            clearTimeout(this.hoverTimeout);
            if (!this.isPanelLocked) {
                this.hoverTimeout = setTimeout(() => {
                    this.resetMarker(marker);
                this.closeInfoPanel();
                }, 100);
            }
        });

        // Gestion du clic
        el.addEventListener('click', (e) => {
            e.stopPropagation();
            clearTimeout(this.hoverTimeout);
            this.isPanelLocked = true;
            this.hoverMarker(marker);
                this.displayInfoPanel(feature, true);
            this.zoomToMarker(marker, true);
        });

        return marker;
    }

    createTownMarker(feature) {
        const coordinates = feature.geometry.coordinates;
        if (!this.areValidCoordinates(coordinates)) return;

        const el = document.createElement('div');
        const distance = 100;

        el.innerHTML = `
            <div class="relative group">
                <div class="w-3 h-3 rounded-full bg-blue shadow-md group-hover:shadow-lg group-hover:z-40"></div>
                <div class="absolute left-1.5 -top-[100px] w-0.5 bg-gradient-to-t from-blue-400/40 to-blue"
                     style="height: ${distance}px;">
                </div>
                <div class="absolute -translate-x-1/2 left-1.5 group-hover:z-40"
                     style="top: -${distance + 35}px">
                    <div class="relative px-2 py-1 bg-white text-blue-900
                              rounded-xl shadow-lg border border-blue/20 whitespace-nowrap
                              group-hover:shadow-xl
                              group-hover:-translate-y-1">
                        <div class="flex items-center gap-2">
                            <span class="font-bold text-sm max-w-[120px] truncate" 
                                  title="${feature.properties.name}">
                                ${feature.properties.name}
                            </span>
                        </div>
                        <div class="absolute w-full left-0 -bottom-5 flex justify-center">
                             <span class="text-xs text-blue-600 bg-white px-2 py-1 
                                       rounded-full shadow-sm border border-blue/10">
                                ${feature.properties.kms} Km
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        `;

        const marker = new maptilersdk.Marker({
            element: el,
            anchor: 'center'
        }).setLngLat(coordinates);
        
        marker._feature = feature;
        return marker;
    }

    cleanupMarkers() {
        console.log("🧹 Nettoyage des marqueurs existants");
        
        // Supprime tous les marqueurs existants de la carte
        this.poiMarkers.forEach(marker => {
            if (marker && marker.remove) {
                marker.remove();
            }
        });
        this.townMarkers.forEach(marker => {
            if (marker && marker.remove) {
                marker.remove();
            }
        });
        
        // Réinitialise les tableaux
        this.poiMarkers = [];
        this.townMarkers = [];
        
        // Réinitialise les états
        this.hoveredMarker = null;
        this.isPanelLocked = false;
        
        // Force le nettoyage des éléments DOM
        const markers = document.querySelectorAll('.poi-marker, .town-marker');
        markers.forEach(marker => {
            if (marker && marker.parentNode) {
                marker.parentNode.removeChild(marker);
            }
        });

        // Nettoyage des sources et couches de la carte si elles existent
        if (this.map) {
            try {
                if (this.map.getLayer('poi-circles')) {
                    this.map.removeLayer('poi-circles');
                }
                if (this.map.getSource('pois')) {
                    this.map.removeSource('pois');
                }
            } catch (error) {
                console.warn("⚠️ Erreur lors du nettoyage des sources:", error);
            }
        }
    }

    displayInfoPanel(feature, isLocked = false) {
        if (!feature || !feature.properties) return;
        
        const infoPanel = this.infoPanelTarget;
        if (!infoPanel) {
            console.error("❌ Impossible de trouver le panneau d'information. Vérifiez l'attribut data-sleep-markers-target='infoPanel'");
            return;
        }
        
        console.log("📍 Affichage du panneau pour:", feature.properties.name);

        // Position du panneau
        const markerPosition = this.map.project(feature.geometry.coordinates);
        const mapContainer = this.map.getContainer();
        const mapWidth = mapContainer.offsetWidth;
        
        // Détermine si le panneau doit être à droite ou à gauche
        const shouldShowOnRight = markerPosition.x < mapWidth / 2;
        
        // Applique les classes de position
        infoPanel.classList.remove('left-4', 'right-4', 'top-24', 'hidden');
        infoPanel.classList.add(
            shouldShowOnRight ? 'left-4' : 'right-4',
            'top-24',
            'block',
            'absolute',
            'z-40'
        );

        // Fly vers le marker avec un offset pour éviter la superposition avec le panneau
        if (isLocked) {
            this.flyToMarker(feature.geometry.coordinates, shouldShowOnRight);
        }
        
        // Met à jour la distance
        const distanceElement = infoPanel.querySelector('[data-distance]');
        if (distanceElement && feature.properties.kms) {
            distanceElement.textContent = feature.properties.kms;
        }

        // Gère l'image partenaire
        const partnerImageContainer = infoPanel.querySelector('[data-partner-image]');
        if (partnerImageContainer) {
            if (feature.properties.type === 'partner' && feature.properties.image_url) {
                const img = partnerImageContainer.querySelector('img');
                if (img) {
                    img.src = feature.properties.image_url;
                    img.alt = feature.properties.name || 'Image hébergement partenaire';
                }
                partnerImageContainer.classList.remove('hidden');
            } else {
                partnerImageContainer.classList.add('hidden');
            }
        }
        
        // Met à jour le contenu
            const contentElement = infoPanel.querySelector('[data-content]');
            if (contentElement) {
                contentElement.innerHTML = `
                    <div class="flex flex-col space-y-3">
                        <div class="space-y-0">
                            <h3 class="text-lg font-bold m-0">${feature.properties.name || 'Sans titre'}</h3>
                            <div class="text-sm text-gray-600">${feature.properties.poi_type || ''}${feature.properties.capacity ? ` / ${feature.properties.capacity} lits` : ''}</div>
                        </div>
                        <div class="flex items-center space-x-2">
                            <span class="font-bold text-primary">${feature.properties.price ? `${feature.properties.price}€` : 'Prix non disponible'}</span>
                        </div>
                        ${feature.properties.url ? `<a href="${feature.properties.url}" 
                            class="text-sm text-primary hover:underline" 
                            data-action="click->sleep-markers#handleDetailsClick"
                            data-return-to-map="true">Voir plus de détails</a>` : ''}
                    </div>
                `;
        }

        // Gestion du clic en dehors du panneau pour le fermer
        if (isLocked) {
            const handleOutsideClick = (e) => {
                if (!infoPanel.contains(e.target) && !e.target.closest('.poi-marker')) {
                    this.closeInfoPanel();
                    document.removeEventListener('click', handleOutsideClick);
                }
            };
            document.addEventListener('click', handleOutsideClick);
        }

        // Gestion du bouton de fermeture
        const closeButton = infoPanel.querySelector('[data-action="sleep-markers#closeInfoPanel"]');
        if (closeButton) {
            closeButton.addEventListener('click', () => this.closeInfoPanel());
        }
    }

    closeInfoPanel() {
        const infoPanel = this.infoPanelTarget;
        if (!infoPanel) return;

        console.log("📍 Fermeture du panneau d'information");
        
        this.isPanelLocked = false;
        infoPanel.classList.add('hidden');
        infoPanel.classList.remove('block', 'absolute');

        // Réinitialise le marqueur survolé et retourne au zoom par défaut
        if (this.hoveredMarker) {
            this.resetMarker(this.hoveredMarker);
            // Utilise le dernier zoom connu au lieu du zoom par défaut
            this.zoomToMarker(this.hoveredMarker, false);
            this.hoveredMarker = null;
        }
    }

    hoverMarker(marker) {
        // Réinitialise le marqueur précédemment survolé s'il existe
        if (this.hoveredMarker && this.hoveredMarker !== marker) {
            this.resetMarker(this.hoveredMarker);
        }

        const element = marker.getElement();
        if (!element) return;

        this.hoveredMarker = marker;  // Stocke le marqueur actuellement survolé
        element.style.zIndex = '1000';
        const iconContainer = element.querySelector('.relative.group');
        if (iconContainer) {
            iconContainer.style.transform = 'scale(1.2)';
            iconContainer.style.transition = 'all 0.2s ease-in-out';
            
            const priceTag = iconContainer.querySelector('div:last-child');
            if (priceTag) {
                priceTag.style.backgroundColor = '#3B82F6';
                priceTag.style.color = '#FFFFFF';
                priceTag.style.borderColor = '#2563EB';
            }
        }
    }

    resetMarker(marker) {
        const element = marker.getElement();
        if (!element) return;
        
        if (this.hoveredMarker === marker) {
            this.hoveredMarker = null;  // Réinitialise la référence au marqueur survolé
        }

        element.style.zIndex = '';
        const iconContainer = element.querySelector('.relative.group');
        if (iconContainer) {
            iconContainer.style.transform = '';
            
            const priceTag = iconContainer.querySelector('div:last-child');
            if (priceTag) {
                priceTag.style.backgroundColor = '';
                priceTag.style.color = '';
                priceTag.style.borderColor = '';
            }
        }
    }

    handleDetailsClick(event) {
        // Utilise matchMedia pour une détection plus fiable des appareils mobiles
        const isMobile = window.matchMedia('(max-width: 768px)').matches;
        if (!isMobile) return;

        event.preventDefault(); // Empêche la navigation par défaut
        
        const url = event.currentTarget.href;
        const state = { returnToMap: true };
        
        // Sauvegarde l'état actuel dans l'historique
        try {
            history.pushState(state, '', url);
            
            // Ajoute un écouteur pour le retour arrière
            const handlePopState = (e) => {
                if (e.state?.returnToMap) {
                    // Trouve le contrôleur toggle-view de manière plus robuste
                    const toggleViewElement = document.querySelector('[data-controller="toggle-view"]');
                    if (toggleViewElement) {
                        const toggleViewController = window.Stimulus.getControllerForElementAndIdentifier(
                            toggleViewElement,
                            'toggle-view'
                        );
                        if (toggleViewController && typeof toggleViewController.showMap === 'function') {
                            toggleViewController.showMap();
                        }
                    }
                }
            };
            
            window.addEventListener('popstate', handlePopState, { once: true });
            
            // Navigation vers l'URL
            window.location.href = url;
        } catch (error) {
            console.error('Erreur lors de la gestion du retour:', error);
            // En cas d'erreur, effectue une navigation classique
            window.location.href = url;
        }
    }

    storePageCoordinates(pageId, features) {
        if (!features || features.length === 0) {
            console.log("❌ Pas de features à stocker pour la page", pageId);
            return;
        }
        
        // Initialise l'objet de stockage s'il n'existe pas
        this.pageCoordinates = this.pageCoordinates || {};
        
        // Crée un bounds incluant tous les POIs de la page
        const pageBounds = new maptilersdk.LngLatBounds();
        
        // Ajoute tous les POIs au bounds pour avoir une meilleure couverture
        features.forEach(feature => {
            if (feature && feature.geometry && feature.geometry.coordinates) {
                pageBounds.extend(feature.geometry.coordinates);
            }
        });
        
        // Étend les bounds avec une marge pour améliorer la navigation
        const sw = pageBounds.getSouthWest();
        const ne = pageBounds.getNorthEast();
        const lngDiff = ne.lng - sw.lng;
        const latDiff = ne.lat - sw.lat;
        
        // Ajoute une marge de 20% pour l'hystérésis
        const margin = 0.2;
        
        const extendedBounds = new maptilersdk.LngLatBounds(
                [sw.lng - lngDiff * margin, sw.lat - latDiff * margin],
                [ne.lng + lngDiff * margin, ne.lat + latDiff * margin]
            );
            
        // Stocke les coordonnées, les bounds et les features pour cette page
        this.pageCoordinates[pageId] = {
            bounds: extendedBounds,
            features: features
        };
        
        console.log(`✅ Coordonnées stockées pour la page ${pageId}`);
    }

    areValidCoordinates(coordinates) {
        return Array.isArray(coordinates) && 
               coordinates.length === 2 && 
               !isNaN(coordinates[0]) && 
               !isNaN(coordinates[1])
    }

    getPadding() {
        return { top: 50, bottom: 50, left: 50, right: 50 }
    }

    calculateInitialBounds() {
        if (!this.map || !this.initialPOIs || !this.initialPOIs.features) return;

        const bounds = new maptilersdk.LngLatBounds();
        
        this.initialPOIs.features.forEach(feature => {
            if (feature.geometry && feature.geometry.coordinates) {
                bounds.extend(feature.geometry.coordinates);
            }
        });

        if (!bounds.isEmpty()) {
            this.initialBounds = bounds;
            
            // Vérifie si c'est un chargement Turbo
            const isTurboLoad = document.documentElement.hasAttribute('data-turbo-loading');
            
            if (!isTurboLoad) {
                // Pour le chargement initial uniquement, on positionne la carte
                this.map.fitBounds(bounds, {
                    padding: this.getPadding(),
                    maxZoom: this.getMaxZoom(),
                    minZoom: this.getMinZoom(),
                    duration: 0
                });
            }
        }
    }

    updatePoiSource(geojson) {
        if (!this.map) return

        try {
            // Supprime la source existante si elle existe
            if (this.map.getSource('pois')) {
                if (this.map.getLayer('poi-circles')) {
                    this.map.removeLayer('poi-circles')
                }
                this.map.removeSource('pois')
            }

            // Ajoute la nouvelle source
            this.map.addSource('pois', {
                type: 'geojson',
                data: geojson
            })

            // Ajoute la nouvelle couche
            this.map.addLayer({
                id: 'poi-circles',
                type: 'circle',
                source: 'pois',
                paint: {
                    'circle-radius': 8,
                    'circle-color': '#3B82F6',
                    'circle-opacity': 0.6
                }
            })
        } catch (error) {
            console.warn("⚠️ Erreur lors de la mise à jour de la source:", error)
        }
    }

    // Restauration des fonctions mobiles
    handleViewModeChanged(event) {
        if (!this.map || !this.initialBounds || this.initialBounds.isEmpty()) {
            console.warn("⚠️ Carte ou bounds non disponibles pour l'adaptation");
            return;
        }
        
        const isMobile = event.detail.isMobile;
        
        const options = {
            padding: this.getPadding(),
            duration: 500
        };
        
        if (!isMobile) {
            options.maxZoom = this.getMaxZoom();
            options.minZoom = this.getMinZoom();
        }
        
        this.map.fitBounds(this.initialBounds, options);
        this.applyMaxBounds(this.initialBounds);
    }
    
    handleMapViewShown() {
        if (!this.map) return;
        
        setTimeout(() => {
            this.map.resize();
            
            if (this.initialBounds && !this.initialBounds.isEmpty()) {
                this.map.fitBounds(this.initialBounds, {
                    padding: this.getPadding(),
                    duration: 500
                });
                
                this.applyMaxBounds(this.initialBounds);
            }
        }, 300);
    }

    // Gestion des événements tactiles
    disableTouchEvents() {
        const mapContainer = this.map?.getContainer();
        if (!mapContainer) return;
        
        this.savedTouchStart = mapContainer.ontouchstart;
        this.savedTouchMove = mapContainer.ontouchmove;
        this.savedTouchEnd = mapContainer.ontouchend;
        
        const preventTouch = (e) => {
            if (e.target.closest('#boundary-message') || e.target.closest('.poi-marker')) return;
            e.preventDefault();
            e.stopPropagation();
        };
        
        mapContainer.ontouchstart = preventTouch;
        mapContainer.ontouchmove = preventTouch;
        mapContainer.ontouchend = preventTouch;
    }
    
    enableTouchEvents() {
        const mapContainer = this.map?.getContainer();
        if (!mapContainer) return;
        
        mapContainer.ontouchstart = this.savedTouchStart;
        mapContainer.ontouchmove = this.savedTouchMove;
        mapContainer.ontouchend = this.savedTouchEnd;
    }

    applyMaxBounds(bounds) {
        if (!this.map || !bounds) return;
        
        try {
            this.map.setMaxBounds(null);
        
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const lngDiff = ne.lng - sw.lng;
        const latDiff = ne.lat - sw.lat;
        
            const margin = 1.0;
        
            const extendedBounds = new maptilersdk.LngLatBounds(
            [sw.lng - lngDiff * margin, sw.lat - latDiff * margin],
            [ne.lng + lngDiff * margin, ne.lat + latDiff * margin]
        );
            
            this.map.setMaxBounds(extendedBounds);
        } catch (error) {
            if (this.map) {
                this.map.setMaxBounds(null);
            }
        }
    }

    getMinZoom() {
        return window.matchMedia('(max-width: 768px)').matches ? 9 : 9;
    }

    getMaxZoom() {
        return window.matchMedia('(max-width: 768px)').matches ? 18 : 16;
    }

    // Vérifie les cartes de POI et ajoute les événements de hover
    checkPoiCards() {
        const poiCards = document.querySelectorAll('[data-poi-id]');
        if (poiCards.length === 0) return;
        
        poiCards.forEach(card => {
            const poiId = card.getAttribute('data-poi-id');
            if (!poiId) return;
            
            // Supprime les anciens événements
            card.removeEventListener('mouseenter', this.hoverCardHandler);
            card.removeEventListener('mouseleave', this.leaveCardHandler);
            
            // Ajoute les nouveaux événements avec délai
            card.addEventListener('mouseenter', () => {
                clearTimeout(this.hoverTimeout);
                this.hoverTimeout = setTimeout(() => {
                    const marker = this.findMarkerByPoiId(poiId);
                    if (marker && !this.isPanelLocked) {
                        // Réinitialise le marqueur précédent si nécessaire
                        if (this.hoveredMarker && this.hoveredMarker !== marker) {
                            this.resetMarker(this.hoveredMarker);
                        }
                        this.hoverMarker(marker);
                        
                        // Vérifie si le marqueur est visible dans la vue actuelle
                        const coordinates = marker._feature.geometry.coordinates;
                        const point = this.map.project(coordinates);
                        const mapContainer = this.map.getContainer();
                        const isVisible = point.x >= 0 && point.x <= mapContainer.offsetWidth &&
                                        point.y >= 0 && point.y <= mapContainer.offsetHeight;
                        
                        // Si le marqueur n'est pas visible, on force le flyTo
                        if (!isVisible) {
                            this.map.flyTo({
                                center: coordinates,
                                zoom: this.hoverZoom,
                                duration: 1000,
                                essential: true,
                                easing: t => t * (2 - t)
                            });
                        } else {
                            this.zoomToMarker(marker, true);
                        }
                    }
                }, 150); // Délai de 150ms
            });
            
            card.addEventListener('mouseleave', () => {
                clearTimeout(this.hoverTimeout);
                const marker = this.findMarkerByPoiId(poiId);
                if (marker && !this.isPanelLocked) {
                    this.hoverTimeout = setTimeout(() => {
                        this.resetMarker(marker);
                        this.zoomToMarker(marker, false);
                    }, 100);
                }
            });
        });
    }

    // Trouve un marker par son POI ID
    findMarkerByPoiId(poiId) {
        return this.poiMarkers.find(marker => {
            const element = marker.getElement();
            return element && element.getAttribute('data-poi-id') === poiId;
        });
    }

    flyToMarker(coordinates, shouldShowOnRight) {
        if (!this.map || !coordinates) return;

        // Annule le flyTo précédent s'il existe
        clearTimeout(this.flyTimeout);

        // Vérifie si le marqueur est visible dans la vue actuelle
        const point = this.map.project(coordinates);
        const mapContainer = this.map.getContainer();
        const isVisible = point.x >= 0 && point.x <= mapContainer.offsetWidth &&
                         point.y >= 0 && point.y <= mapContainer.offsetHeight;

        // Si le marqueur n'est pas visible, on ajuste le zoom pour le rendre visible
        if (!isVisible) {
            this.flyTimeout = setTimeout(() => {
                this.map.flyTo({
                    center: coordinates,
                    zoom: this.hoverZoom,
                    duration: 1500,
                    essential: true,
                    easing: t => t * (2 - t)
                });
            }, 500);
            return;
        }

        // Si le marqueur est visible, on applique l'offset pour le panneau
        const panelWidth = 320;
        const offset = shouldShowOnRight ? panelWidth/2 : -panelWidth/2;

        this.flyTimeout = setTimeout(() => {
            this.map.flyTo({
                center: coordinates,
                offset: [offset, 0],
                zoom: this.map.getZoom(),
                duration: 1500,
                essential: true,
                easing: t => t * (2 - t)
            });
        }, 500);
    }

    zoomToMarker(marker, isHovering) {
        if (!this.map || !marker._feature?.geometry?.coordinates) return;

        // Annule le timeout précédent s'il existe
        if (this.zoomTimeout) {
            clearTimeout(this.zoomTimeout);
        }

        const targetZoom = isHovering ? this.hoverZoom : this.defaultZoom;
        const currentZoom = this.map.getZoom();
        
        // Si le zoom actuel est déjà proche du zoom cible, ne fait rien
        if (Math.abs(currentZoom - targetZoom) < 0.5) return;

        // Stocke le zoom actuel pour éviter les va-et-vients
        this.lastZoom = currentZoom;

        this.map.flyTo({
            center: marker._feature.geometry.coordinates,
            zoom: targetZoom,
            duration: 1200,
            essential: true,
            easing: t => t * (2 - t)
        });
    }
}